// Listing date fields (data.lastUpdated / data.publishDate) are stored in
// Builder as free-text strings in two shapes: ISO ('2022-08-26T12:00:00.000Z')
// and JS Date.toString() output ('Thu Jun 18 2026 20:00:00 GMT-0400 (Eastern
// Daylight Time)'). Only ISO is guaranteed parseable by every engine. V8
// (Node + Chrome) also parses the toString shape, but JavaScriptCore (Safari)
// and SpiderMonkey (Firefox) can return Invalid Date for the trailing
// "(Eastern Daylight Time)" timezone name. A NaN here would poison the whole
// listing sort comparator (NaN comparisons give implementation-defined order),
// so parse deterministically across engines and never return NaN.
export const parseListingDate = (value?: string | number | null): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const direct = Date.parse(value);
  if (!Number.isNaN(direct)) {
    return direct;
  }

  // Drop the trailing "(...)" timezone name that non-V8 engines reject. The
  // numeric GMT offset is kept, so the absolute instant is preserved.
  const withoutTzName = value.replace(/\s*\([^)]*\)\s*$/, '');
  const stripped = Date.parse(withoutTzName);

  return Number.isNaN(stripped) ? 0 : stripped;
};

// Always formatted in UTC. Without a timeZone, toLocaleDateString uses the
// runtime's zone: the server renders in UTC on Vercel, then the browser
// re-renders in the visitor's zone, so anyone west of UTC sees the day before
// and React throws hydration errors #418/#425 on every blog and press post.
//
// UTC is also the day the author chose, for every shape Builder stores:
// - the date picker saves midnight UTC, serialised in a US browser as the
//   previous evening ("Thu Jun 18 2026 20:00:00 GMT-0400" = Jun 19 00:00 UTC);
// - hand-typed values are midnight or daytime in a US zone, the same day in UTC;
// - ISO values are midnight, noon or daytime UTC.
// Checked against all 810 toString values and 137 ISO values live in Builder.
export const getCleanDate = (date: string, option?: Intl.DateTimeFormatOptions) => {
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    throw new Error('Invalid date string');
  }

  return parsedDate.toLocaleDateString('en-US', {
    ...(option || {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
    }),
    timeZone: 'UTC',
  });
};
